import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  delay,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import * as actions from './feed-profit.actions';
import { ProductModuleState } from '../../product/models/product.model';
import * as settingActions from '../../settings/store/actions/settings.actions';
import { Calculation } from '../models/feed-profit.model';
import { FeedProfitService } from '../services/feed-profit.service';

@Injectable()
export class FeedProfitEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly store: Store<ProductModuleState>,
    private readonly feedProfitService: FeedProfitService,
    private readonly dialogService: DialogService,
    private readonly localeService: LocaleService,
  ) {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadData),
      exhaustMap(() =>
        of(
          actions.clearFeedProfitState(),
          actions.loadCalculationsRequest(),
          settingActions.loadDiscountRequest(),
        ).pipe(delay(100)),
      ),
    ),
  );

  loadCalculations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadCalculationsRequest),
      exhaustMap((_) =>
        this.feedProfitService.getCalculations().pipe(
          switchMap((calculations: Calculation[]) =>
            of(actions.loadCalculationsSuccess({ calculations })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadCalculationsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertCalculationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.upsertCalculationRequest),
      exhaustMap(({ calculation }) =>
        this.feedProfitService.upsertCalculation(calculation).pipe(
          map((response: number) =>
            actions.upsertCalculationSuccess({
              calculation: {
                ...calculation,
                id: response,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.upsertCalculationError({ error }),
              actions.loadCalculationsRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertCalculationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.upsertCalculationSuccess),
        map((action) => action.calculation),
        tap((calculation) => {
          const urlParts = this.router.url.split('?');
          const url = urlParts.length > 0 ? urlParts[0] : this.router.url;
          if (url.indexOf('/feed-profit/create') >= 0) {
            this.store.dispatch(actions.setSelectedCalculationSuccess({ calculation }));
            this.ngZone.run(() => this.router.navigate(['/feed-profit', calculation.id])).then();
          } else {
            this.store.dispatch(
              actions.updateSelectedCalculation({
                values: {
                  ...calculation,
                },
              }),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  deleteCalculationWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteCalculationWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate('feed-profit.overview.delete-confirmation.title'),
            text: this.localeService.translate('feed-profit.overview.delete-confirmation.message'),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  actions.deleteCalculationRequest({
                    calculationId: action.calculationId,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteCalculationRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteCalculationRequest),
      mergeMap((action) =>
        this.feedProfitService.deleteCalculation(action.calculationId).pipe(
          map(() => actions.deleteCalculationSuccess({ calculationId: action.calculationId })),
          catchError((_: HttpErrorResponse) =>
            of(
              NotificationActions.errorSnackbar({
                message: this.localeService.translate('feed-profit.overview.delete-error-message'),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteCalculationSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.deleteCalculationSuccess),
        tap((_) => this.router.navigate(['/feed-profit'])),
      ),
    { dispatch: false },
  );

  /* NAVIGATION EFFECTS */
  navigateCalculationDetail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.navigationCalculationDetail),
        map((action) => {
          this.store.dispatch(
            actions.setNavigateCalculationSuccess({ calculationId: action.calculationId }),
          );
          return action;
        }),
        tap((action) => this.router.navigate(['feed-profit', action.calculationId])),
      ),
    {
      dispatch: false,
    },
  );
}
